import {
  getPriceRange,
  type PriceCategoryKey,
} from "~/components/outfit-product/price-ranges";
import type { Currency } from "~/lib/currencies";
import type { CurrencyPrices } from "~/lib/types";
import { isValidNumber } from "~/lib/utils";

interface SimilarProduct {
  product_id: string;
  price_range: string;
  price: CurrencyPrices;
}

interface Params {
  matches: SimilarProduct[];
  category: string;
  currency: Currency;
  targetRange?: string | null;
}

export interface PriceAndRange {
  price?: number;
  min?: number;
  max?: number;
}

export function calcPriceAndRange({
  matches,
  category,
  currency,
  targetRange,
}: Params): PriceAndRange {
  const similar =
    matches.find((p) => p.price_range === targetRange) ?? matches[0];

  if (!similar) return {};

  const priceInCurrency = similar.price[currency.code];

  const priceCategory = category as PriceCategoryKey;
  if (!priceCategory) {
    return { price: priceInCurrency };
  }

  // find the max upper price_range for the product_id in similar & similar_products
  // basically, some products might not exist at the price range specified by the user
  // e.g. if the user specified a low price range for a suit, an adequete suit might not exist at that price range
  // yet we want to initialise the price range to be in accordance with their preferences,
  // we need to search for items that exist given the bounds of similar products.
  // So we should set lowerBound to the lower bound of user price prefs, and upper bound to the max of the price_range from similar

  // There may be duplicate products in similar_products -- this happens when an item does not exist in the price category specified by the user
  // here, we're getting the all the similar duplicated products
  const sameProductIdsAsSimilar = matches.filter(
    (p) => p.product_id === similar?.product_id,
  );

  // and now we're finding the highest price range
  // this will serve as the upper bound price range
  // as an example, if the user's selected price category is "$",
  // but the most similar product exists in $, $$ and $$$
  // it means that there was no adequetely similar product in the $ or $$ price category
  // so the closest matching product has a price range in the $$$ category
  // this line will find what that price range is (e.g. "$$$")
  const maxPriceRangeLabel =
    sameProductIdsAsSimilar[sameProductIdsAsSimilar.length - 1]?.price_range;

  // Find lowest price range
  const lowerBound = getPriceRange(priceCategory, similar.price_range)?.min;
  if (!isValidNumber(lowerBound)) {
    return { price: priceInCurrency };
  }

  // Find highest price, from the upper bound of the most similar product
  const upperBound = getPriceRange(priceCategory, maxPriceRangeLabel)?.max;
  if (!isValidNumber(upperBound)) {
    return { price: priceInCurrency };
  }

  return {
    price: priceInCurrency,
    min: lowerBound,
    max: upperBound,
  };
}
